/**
 * O dialeto Markdown dos documentos Westhetics (docs/styleguide/08-legal-design.md).
 *
 * CommonMark + GFM + front matter + diretivas (`remark-directive`) + variáveis `{{path}}`.
 * Cada diretiva tem um significado de legal design e um título padrão. Quem escreve escolhe o
 * bloco pelo significado; quando o caso pede outro rótulo, troca só o título com o atributo
 * `title` (`:::summary{title="O que você precisa saber"}`, T40). Assim todo documento da
 * Westhetics fala a mesma língua, e o rótulo se adapta ao público.
 */

export type DirectiveType = 'containerDirective' | 'leafDirective' | 'textDirective'

export interface DirectiveDefinition {
  name: string
  type: DirectiveType
  /**
   * Rótulo padrão impresso no documento; o atributo `title` do bloco troca (ver `getBlockTitle`).
   * `null` quando a diretiva não tem título (quebra de página, termo, sublinhado).
   */
  title: string | null
  /** Para quem escreve: o que vai dentro. */
  description: string
  /** Atributos aceitos (`:::update{date="2026-08-12"}`, `:::risks{title="…"}`). */
  attributes?: string[]
  /**
   * Bloco que só a API monta (a galeria do plano de tratamento, P-PLA-11): o editor preserva e
   * mostra, mas não oferece no menu "Bloco", porque a clínica não escreve esse documento.
   */
  apiOnly?: boolean
}

/** O atributo que troca o título padrão de um bloco (T40). Todo bloco de contêiner aceita. */
export const BLOCK_TITLE_ATTRIBUTE = 'title'

export const DIRECTIVES: readonly DirectiveDefinition[] = [
  {
    name: 'summary',
    type: 'containerDirective',
    title: 'Em resumo',
    description:
      'Em linguagem clara, antes do texto jurídico: o que vai acontecer, quem faz e o que este documento combina. Uma ou duas frases.',
    attributes: [BLOCK_TITLE_ATTRIBUTE],
  },
  {
    name: 'authorization',
    type: 'containerDirective',
    title: 'O que você está autorizando',
    description:
      'O procedimento, a região e o que foi combinado na avaliação. Frase única, em destaque.',
    attributes: [BLOCK_TITLE_ATTRIBUTE],
  },
  {
    name: 'risks',
    type: 'containerDirective',
    title: 'O que pode acontecer',
    description:
      'Os efeitos esperados e os riscos descritos de forma explícita, inclusive o que não caracteriza erro (assimetria leve, retoque).',
    attributes: [BLOCK_TITLE_ATTRIBUTE],
  },
  {
    name: 'attention',
    type: 'containerDirective',
    title: 'Atenção',
    description:
      'O que exige decisão ou cuidado antes de agir. Fio plum. Use pouco: quando aparece, é lido.',
    attributes: [BLOCK_TITLE_ATTRIBUTE],
  },
  {
    name: 'context',
    type: 'containerDirective',
    title: 'Por que isso está aqui',
    description: 'Explica a razão de uma cláusula sem carregar risco. Fio lilás.',
    attributes: [BLOCK_TITLE_ATTRIBUTE],
  },
  {
    name: 'parties',
    type: 'containerDirective',
    title: 'Partes',
    description: 'Lista com **Papel**: qualificação. Uma linha por parte.',
    attributes: [BLOCK_TITLE_ATTRIBUTE],
  },
  {
    name: 'signatures',
    type: 'containerDirective',
    title: 'Assinaturas',
    description: 'Lista com Papel: nome. Vira linhas de assinatura; no PDF, cada uma com o traço.',
    attributes: [BLOCK_TITLE_ATTRIBUTE],
  },
  {
    name: 'update',
    type: 'containerDirective',
    title: 'Cláusula atualizada',
    description: 'O motivo da mudança em uma linha, com a data. É o que faz o documento ser vivo.',
    attributes: ['date', BLOCK_TITLE_ATTRIBUTE],
  },
  {
    name: 'gallery',
    type: 'containerDirective',
    // Sem título padrão: imprime só o do atributo `title` ("Marcação", "Frasco · Caixa · Etiqueta").
    title: null,
    description:
      'Grade de fotos com legenda, no plano de tratamento (P-PLA-11). Cada imagem `![Legenda](src)` vira uma figura; o parágrafo curto logo abaixo dela (data e quem registrou) é a segunda linha da legenda. `columns` é 2 ou 3 (padrão 3); `empty` é o texto impresso quando não há foto. A API monta este bloco; a clínica não edita.',
    attributes: [BLOCK_TITLE_ATTRIBUTE, 'columns', 'empty'],
    apiOnly: true,
  },
  {
    name: 'anamnesis',
    type: 'leafDirective',
    title: 'Anamnese',
    description:
      'As perguntas e respostas da anamnese respondida que serve de base ao atendimento (P-DOC-03): uma seção por bloco do questionário, cada pergunta com a resposta por extenso, e no fim o protocolo, a data e o hash. `empty` é o texto impresso quando o atendimento não tem anamnese respondida. No modo de ficha em branco (RN-ANA-24), as mesmas seções saem como formulário para preencher à mão: os dados da paciente em linhas, cada pergunta numerada com caixas para marcar e linhas para escrever. A API monta este bloco; a clínica não edita.',
    attributes: [BLOCK_TITLE_ATTRIBUTE, 'empty'],
    apiOnly: true,
  },
  {
    name: 'page-break',
    type: 'leafDirective',
    title: null,
    description: 'Quebra de página no PDF. Sem efeito na tela.',
  },
  {
    name: 'term',
    type: 'textDirective',
    title: null,
    description:
      'Termo definido em linha: :term[ácido hialurônico]{def="…"}. Na tela mostra a definição ao toque; no PDF, entre parênteses.',
    attributes: ['def'],
  },
  {
    name: 'u',
    type: 'textDirective',
    title: null,
    description:
      'Sublinhado em linha: :u[texto]. Pedido do cliente em 15/10/2026 (T40); vale na tela, no HTML e no PDF. Use pouco: o destaque do documento é a estrutura, não o traço.',
  },
] as const

export const DIRECTIVE_NAMES = new Set(DIRECTIVES.map((d) => d.name))

export function getDirectiveDefinition(name: string) {
  return DIRECTIVES.find((d) => d.name === name)
}

/**
 * O título impresso de um bloco: o do atributo `title`, quando quem escreve trocou o rótulo, senão
 * o padrão do dialeto. Vazio ou só espaço volta ao padrão. `null` para diretiva sem título ou fora
 * do dialeto.
 */
export function getBlockTitle(
  name: string,
  attributes?: Record<string, string | null | undefined> | null,
): string | null {
  const definition = getDirectiveDefinition(name)
  if (!definition) return null
  const custom = attributes?.[BLOCK_TITLE_ATTRIBUTE]?.trim()
  return custom || definition.title
}

/** Blocos que um termo de consentimento precisa ter, na ordem em que o paciente lê. */
export const REQUIRED_CONSENT_FORM_BLOCKS = ['summary', 'authorization', 'risks'] as const

/** Colunas que a galeria aceita e a que vale quando o atributo falta ou está fora da lista. */
export const GALLERY_COLUMNS = [2, 3] as const
export type GalleryColumns = (typeof GALLERY_COLUMNS)[number]
export const DEFAULT_GALLERY_COLUMNS: GalleryColumns = 3

/** O atributo que diz o que a galeria imprime quando não há foto ("Sem foto registrada nesta etapa"). */
export const GALLERY_EMPTY_ATTRIBUTE = 'empty'

/**
 * As colunas de uma galeria: `columns=2` ou `columns=3`; qualquer outro valor (ou nenhum) é o
 * padrão. A API monta o bloco, então valor estranho não vira aviso: vira a grade padrão.
 */
export function getGalleryColumns(
  attributes?: Record<string, string | null | undefined> | null,
): GalleryColumns {
  const value = Number(attributes?.columns)
  return (GALLERY_COLUMNS as readonly number[]).includes(value)
    ? (value as GalleryColumns)
    : DEFAULT_GALLERY_COLUMNS
}

/** O atributo que diz o que `::anamnesis` imprime sem anamnese respondida. */
export const ANAMNESIS_EMPTY_ATTRIBUTE = 'empty'
/** O texto padrão de `::anamnesis{empty}`. */
export const DEFAULT_ANAMNESIS_EMPTY_TEXT = 'Sem anamnese respondida neste atendimento'

/** Os textos fixos do bloco `::anamnesis` (P-DOC-03): quem escreve não os troca. */
export const ANAMNESIS_TEXTS = {
  /** Pergunta sem resposta. */
  unanswered: 'Não respondido',
  /** A paciente preferiu não responder. */
  skipped: 'Pulada',
  /** O rótulo do "descreva", antes do texto. */
  note: 'Observação',
  /** Os rótulos da linha final: "Protocolo … · respondida em … · hash …". */
  protocol: 'Protocolo',
  completedAt: 'respondida em',
  hash: 'hash',
} as const

/**
 * Os textos fixos da ficha em branco (RN-ANA-24, P-ANA-10), o modo de formulário de `::anamnesis`:
 * os rótulos dos dados da paciente, as instruções de marcar e as frases da condição e do "descreva".
 * Quem escreve não os troca.
 */
export const ANAMNESIS_BLANK_TEXTS = {
  /** O título da seção de identificação, antes dos blocos do questionário. */
  identification: 'Seus dados',
  /** Os rótulos dos campos de identificação, na ordem impressa. */
  fields: {
    name: 'Nome completo',
    birthDate: 'Data de nascimento',
    docNumber: 'CPF',
    phone: 'Telefone (WhatsApp)',
    email: 'E-mail',
    address: 'Endereço',
  },
  /** Escolha única e escala. */
  singleChoice: 'Marque uma.',
  /** Escolha múltipla. */
  multipleChoice: 'Marque todas que se aplicam.',
  /** A caixa a mais da pergunta que pode ficar em branco. */
  skip: 'Prefiro não responder',
  /** Depois do rótulo do "descreva" que não é obrigatório. */
  optional: 'opcional',
  /** "Responda se marcou Sim na anterior." · "Responda se marcou Sim ou Não sei dizer na pergunta 4." */
  conditionPrefix: 'Responda se marcou',
  conditionPrevious: 'na anterior',
  conditionQuestion: 'na pergunta',
  /** "Se marcou Sim: A quê?" */
  followUpPrefix: 'Se marcou',
  /** Entre as respostas de uma condição. */
  or: 'ou',
  /** Em `yes_no` sem opções. */
  yes: 'Sim',
  no: 'Não',
} as const

/** Os tipos de documento em que `::anamnesis` cabe; em outro, o lint avisa (`misplaced_directive`). */
export const ANAMNESIS_DOCUMENT_TYPES: readonly DocumentType[] = [
  'anamnesis',
  'contract',
  'consent_form',
]

export type DocumentType =
  'consent_form' | 'contract' | 'anamnesis' | 'standalone' | 'treatment_plan' | 'other'

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  consent_form: 'Termo de consentimento',
  // Cobre também o contrato que não é de procedimento (curso, trabalho, parceria; RN-DOC-42):
  // o rótulo é o genérico, e o título do documento diz qual contrato é.
  contract: 'Contrato',
  anamnesis: 'Anamnese',
  standalone: 'Termo',
  // O PDF do plano (RN-PLA-28, P-PLA-11): a API monta o Markdown a partir do plano; a clínica não
  // edita. Sem bloco obrigatório.
  treatment_plan: 'Plano de tratamento',
  other: 'Documento',
}

/** Os tipos que o front matter aceita, na ordem do rótulo. */
export const DOCUMENT_TYPES = Object.keys(DOCUMENT_TYPE_LABELS) as DocumentType[]

/**
 * `{{ patient.name }}` — caminho com letras, números, ponto e sublinhado, e índice `[n]`
 * (`{{clinic.witnesses[1].name}}`, P-DOC-01). O índice também casa escapado (`\[1]`), que é
 * como o `MarkdownEditor` salva o colchete em texto corrido (mdast-util-to-markdown); o caminho
 * capturado passa por `normalizeVariablePath` antes de ser usado.
 */
export const VARIABLE_REGEX = /\{\{\s*([a-zA-Z_](?:[\w.]|\\?\[\d+\\?\])*)\s*\}\}/g
